import { AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * WHAT AN ARMED DELETE IS ABOUT TO STRAND, where a teacher can actually see it.
 *
 * `useDrivenRowDeleteWarning` has said this since the axis shipped, but only into the armed
 * button's `aria-label` and `title` — so a sighted teacher saw an armed trash icon and
 * nothing else. Reported as "i don't see any warnings on the leading block when i'm about to
 * delete or edit options".
 *
 * It was deliberate: `feedback_arm_hint_layout_shift` records that anything mounting beside a
 * two-click delete inside a scroller moves the button out from under a stationary cursor, and
 * the resulting `pointerleave` disarms it before the second click can land. The conclusion
 * drawn then — ride the label, cost no layout — made the warning invisible, which is not a
 * warning.
 *
 * `VersionSelector` had already solved the same problem the other way and is the one to copy:
 * show it, and ignore a `pointerleave` for a moment after arming. Reserving permanent space
 * for a rare warning was considered there and rejected for the same reason it would be here —
 * every option row in the editor carrying a blank gap for something almost none of them will
 * ever say.
 */
export function StrandWarning({ text }: { text: string }) {
  return (
    <span className='flex items-center gap-1 rounded-md border border-ws-warning-fg bg-ws-warning-bg/50 px-1.5 py-0.5 text-[11px] font-medium text-ws-warning-fg'>
      {/* The symbol, not the colour: a warning is a triangle everywhere in this editor, and
          colour alone is not a signal — see `feedback_error_symbol_not_colour_alone`. */}
      <AlertTriangle aria-hidden='true' className='h-3 w-3 shrink-0' />
      {/* `aria-hidden`, because the armed button's own label already says this and a reader
          meeting it twice is told once too often. */}
      <span aria-hidden='true'>{text}</span>
    </span>
  );
}

/** Long enough for the reflow the warning causes to settle — `VersionSelector`'s own figure. */
const ARM_SETTLE_MS = 300;

/**
 * May a `pointerleave` disarm yet?
 *
 * False for a moment after arming, because the warning this hook exists to allow is what
 * moves the button. A real pointer that leaves within the window simply stays armed, which
 * costs nothing: blur, Escape and a click elsewhere all still disarm, and an armed button
 * does nothing on its own.
 *
 * The timestamp is written in an EFFECT and never during render. A ref mutated on the way
 * through a render survives a render React then discards, and this one would then report
 * "settled" for an arming that never committed — see `feedback_render_phase_refs_rollback_hazard`.
 */
export function useArmSettle(armed: boolean): () => boolean {
  const armedAt = useRef(0);
  useEffect(() => { if (armed) armedAt.current = Date.now(); }, [armed]);
  return useCallback(() => Date.now() - armedAt.current >= ARM_SETTLE_MS, []);
}
